import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, PackageX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProductImage } from "./ProductImage";
import { useLanguage } from "@/hooks/useLanguage";
import { pickLocalized } from "@/lib/localized";
import {
  DEFAULT_ANNOUNCEMENT_BACKGROUND,
  DEFAULT_ANNOUNCEMENT_TEXT,
} from "@/lib/announcementColors";
import { useAnnouncements } from "@/lib/AnnouncementsContext";
import type { Announcement } from "@/lib/api";
import { SelectionCircle } from "./SelectionCircle";

const STATUS_BADGES: Record<string, string> = {
  draft: "badge-warning",
  hidden: "badge-neutral",
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  published: "products.statusPublished",
  draft: "products.statusDraft",
  hidden: "products.statusHidden",
  unavailable: "products.statusUnavailable",
};

// Status pill (draft / hidden / unavailable) shared by ProductCard and
// ProductDetail. The parent must be `relative`; published products get none.
export function StatusBadge({ status }: { status?: string }) {
  const { t } = useTranslation();
  if (!status) return null;
  const label = STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]!) : status;

  if (status === "unavailable") {
    return (
      <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/85 px-3 py-1 text-xs font-light uppercase tracking-wide leading-tight text-white shadow-md ring-1 ring-white/25 backdrop-blur-sm">
        <PackageX size={14} strokeWidth={2.25} aria-hidden="true" />
        {label}
      </span>
    );
  }

  const badgeClass = STATUS_BADGES[status];
  if (!badgeClass) return null;
  return (
    <span className={`badge ${badgeClass} absolute top-2 right-2 px-2 capitalize z-10`}>
      {label}
    </span>
  );
}

// Diagonal corner ribbon shared by ProductCard and ProductDetail. The parent
// must be `relative` - the ribbon crosses its top-left corner. Sized so a
// two-word announcement wraps onto a second line and still stays fully
// inside the clip box rather than getting cut off. `size="lg"` scales
// everything up (proportionally, so the diagonal still lines up) for the
// bigger product-detail image; cards use the smaller default.
const RIBBON_SIZES = {
  sm: {
    box: "w-28 h-28",
    band: "top-8 -left-9 w-40 py-1.5 text-[9px]",
  },
  lg: {
    box: "w-44 h-44",
    band: "top-12 -left-14 w-64 py-2 text-base",
  },
};

export function AnnouncementRibbon({
  announcement,
  size = "sm",
}: {
  announcement?: Announcement | null;
  size?: "sm" | "lg";
}) {
  const { currentLanguage } = useLanguage();
  // Prefer the shared cache over the copy embedded in the product fetch -
  // an edit made in AnnouncementsModal (or the product form's quick color
  // picker) lands here immediately, without refetching the product.
  const { byId } = useAnnouncements();
  if (!announcement) return null;
  const live = byId.get(announcement.id) ?? announcement;
  const text = pickLocalized(live.message, live.message_en, currentLanguage);
  const { box, band } = RIBBON_SIZES[size];

  return (
    <div className={`absolute top-0 left-0 z-5 overflow-hidden pointer-events-none ${box}`}>
      <span
        className={`absolute text-center font-bold uppercase tracking-wide -rotate-45 shadow whitespace-normal wrap-normal leading-tight ${band}`}
        style={{
          backgroundColor: live.background_color ?? DEFAULT_ANNOUNCEMENT_BACKGROUND,
          color: live.text_color ?? DEFAULT_ANNOUNCEMENT_TEXT,
        }}
      >
        {text}
      </span>
    </div>
  );
}

export function ProductCard({
  name,
  brand,
  status,
  announcement,
  variantAnnouncements,
  // One entry per variant, in order. Per-entry: undefined = still resolving
  // that signed URL, null = resolved, no image.
  imageUrls = [],
  selected,
  onToggleSelect,
  onVisibleVariantChange,
  initialVariantIndex = 0,
}: {
  name: string;
  brand: string;
  status?: string;
  announcement?: Announcement | null;
  // Optional per-image announcements. Home uses this to keep the ribbon in
  // sync with its variant carousel; callers that omit it retain the existing
  // single product-level announcement behaviour.
  variantAnnouncements?: (Announcement | null)[];
  imageUrls?: (string | null | undefined)[];
  selected?: boolean;
  onToggleSelect?: () => void;
  onVisibleVariantChange?: (index: number) => void;
  initialVariantIndex?: number;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialVariantIndex);
  const count = imageUrls.length;
  const visibleAnnouncement = variantAnnouncements
    ? variantAnnouncements[index] ?? null
    : announcement;

  const changeIndex = (getNextIndex: (current: number) => number) => {
    const next = getNextIndex(index);
    setIndex(next);
    onVisibleVariantChange?.(next);
  };

  // Cards are wrapped in a Link (navigate) or a selectable div (toggle
  // select) by the caller - stop both from firing when paging the carousel.
  const step = (delta: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    changeIndex(current => (current + delta + count) % count);
  };

  // Swipe-to-change on touch devices, as an alternative to tapping the
  // arrow buttons. A tap fires no meaningful touchmove, so the browser's
  // native tap-vs-drag distinction already keeps this from also triggering
  // the card's Link navigation.
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const SWIPE_THRESHOLD = 40;
  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    const touch = e.changedTouches[0];
    if (!start || !touch || count < 2) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    changeIndex(current => (current + (dx < 0 ? 1 : -1) + count) % count);
  };

  return (
    <div
      className={`bg-brand rounded-2xl text-center text-white w-full max-w-55 shadow-xl relative hover:scale-105 transition duration-200 hover:brightness-105 ${
        selected ? "ring-2 ring-offset-1 sm:ring-4 sm:ring-offset-2 ring-offset-app-background ring-brand" : ""
      }`}
    >
      <AnnouncementRibbon announcement={visibleAnnouncement} />
      {onToggleSelect && (
        <button
          type="button"
          aria-label={
            selected
              ? t("products.unselectProduct", { name })
              : t("products.selectProduct", { name })
          }
          aria-pressed={!!selected}
          className="group absolute top-2 left-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full bg-app-control text-foreground shadow"
          onClick={event => {
            event.stopPropagation();
            onToggleSelect();
          }}
        >
          <SelectionCircle checked={!!selected} />
        </button>
      )}
      <StatusBadge status={status} />
      <div
        className="relative w-full aspect-square bg-white rounded-t-2xl overflow-hidden"
        onTouchStart={count > 1 ? onTouchStart : undefined}
        onTouchEnd={count > 1 ? onTouchEnd : undefined}
      >
        {count > 0 ? (
          // All variant pictures sit side by side on one track; paging just
          // translates it, so the change is a smooth slide instead of the
          // single <img>'s src being swapped (which had no animation and a
          // load-triggered opacity flash on top).
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ width: `${count * 100}%`, transform: `translateX(-${index * (100 / count)}%)` }}
          >
            {imageUrls.map((url, i) => (
              <div key={i} className="h-full shrink-0" style={{ width: `${100 / count}%` }}>
                <ProductImage src={url} alt={name} className="w-full h-full" />
              </div>
            ))}
          </div>
        ) : (
          <ProductImage src={null} alt={name} className="w-full h-full" />
        )}
        {count > 1 && (
          <>
            <button
              type="button"
              aria-label={t("productCard.previousPicture")}
              onClick={step(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center size-9 bg-app-control/90 hover:bg-app-control-hover text-brand rounded-full shadow cursor-pointer transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              aria-label={t("productCard.nextPicture")}
              onClick={step(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center size-9 bg-app-control/90 hover:bg-app-control-hover text-brand rounded-full shadow cursor-pointer transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
              {imageUrls.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === index ? "bg-brand" : "bg-brand/30"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="flex flex-col gap-1 py-3 px-3">
        <h3 className="h-12 font-bold text-md break-normal">
          <span className="line-clamp-2">{name}</span>
        </h3>
        <p className="h-5 w-full truncate text-sm text-white/70">{brand}</p>
      </div>
    </div>
  );
}

// Matches ProductCard's shape/sizing so a grid of these reads as "more
// cards sliding in", not a layout jump when the real ones arrive.
export function ProductCardSkeleton() {
  return (
    <div className="bg-brand/40 rounded-2xl w-full max-w-55 shadow-xl">
      <div className="w-full aspect-square rounded-t-2xl bg-white/60 animate-pulse" />
      <div className="flex flex-col items-center gap-1 py-3">
        <div className="h-12">
          <div className="h-5 w-28 rounded bg-white/60 animate-pulse" />
        </div>
        <div className="h-5 w-16 rounded bg-white/40 animate-pulse" />
      </div>
    </div>
  );
}
